import React, { useState, useEffect } from "react";
import { Table, Carousel, Badge, Button, Row, Col } from "react-bootstrap";
import { getChartTitle, getChartDescription, getTeethData, formatTo2Decimals } from '../dentist/Formatter';
import { toothConditions } from "../dentist/data/teethData";
import { formatDisplayDate } from "../../../utils/formatter";
import Tooth from "./Tooth";
import CustomPagination from "../../reusables/CustomPagination";
import AddDentalPaymentForm from "./AddDentalPaymentForm";

const PreviousDentalChart = ({ previousDentalChart, onChange }) => {
    const [currentChartId, setCurrentChartId] = useState(previousDentalChart?.[0]?.chart_id || null);
    const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
    const [selectedTreatment, setSelectedTreatment] = useState(null);
    const [bulkPayments, setBulkPayments] = useState({});
    const [expandedPayments, setExpandedPayments] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
    const amountPaidVia = { 'cash': 'Cash', 'card': 'Card', 'upi': 'UPI', 'other': 'Other' };

    const currentChart = previousDentalChart.find(chart => chart.chart_id === currentChartId) || {};

    const getCondition = (chart, toothNumber) => {
        const toothInfo = (chart.teeth || []).find(t => t.tooth_number === toothNumber);
        const treatments = toothInfo ? toothInfo.treatments : [];
        const completedTreatments = treatments
            .filter(t => t.treatment_status === "completed")
            .sort((a, b) => new Date(b.treatment_date) - new Date(a.treatment_date));
        return completedTreatments.length > 0 ? completedTreatments[0].condition : "healthy";
    };

    const getAllTreatments = (chart) => {
        return (chart.teeth || []).flatMap(tooth =>
            (tooth.treatments || []).map(treatment => ({
                ...treatment,
                tooth_number: tooth.tooth_number
            }))
        );
    };

    const handleOpenAddPaymentForm = (treatment, bulkGroup = null) => {
        if (bulkGroup) {
            setSelectedTreatment({ ...treatment, bulk_group: bulkGroup, isBulk: true });
        } else {
            setSelectedTreatment(treatment);
        }
        setShowAddPaymentForm(true);
    };

    const handleSavePayment = (payment) => {
        if (selectedTreatment?.isBulk && selectedTreatment.bulk_group) {
            const groupId = selectedTreatment.bulk_group;
            const children = getAllTreatments(currentChart).filter(p => p.bulk_group === groupId);
            if (children.length === 0) return;

            const existingBulk = bulkPayments[groupId] || {};
            const existingPayments = existingBulk.dental_payment_history || [];
            const updatedPaymentHistory = [...existingPayments, payment];

            const totalPaidBefore = formatTo2Decimals(existingBulk.paid_amount || 0);
            const totalFinalCost = formatTo2Decimals(existingBulk.final_cost || 0);
            const paymentAmount = formatTo2Decimals(payment.amount || 0);
            const totalPaidAfter = formatTo2Decimals(totalPaidBefore + paymentAmount);
            const totalRemaining = formatTo2Decimals(totalFinalCost - totalPaidAfter);

            const updatedBulk = {
                ...existingBulk,
                bulk_group: groupId,
                paid_amount: totalPaidAfter,
                remaining_balance: Math.max(0, totalRemaining),
                dental_payment_history: updatedPaymentHistory
            };

            const perChildPaid = formatTo2Decimals(totalPaidAfter / children.length);

            const updatedTeeth = (currentChart.teeth || []).map(tooth => ({
                ...tooth,
                treatments: (tooth.treatments || []).map(plan => {
                    if (plan.bulk_group === groupId) {
                        const finalCostPerTooth = formatTo2Decimals(plan.final_cost || 0);
                        const cappedPaid = Math.min(perChildPaid, finalCostPerTooth);
                        const newRemaining = formatTo2Decimals(Math.max(0, finalCostPerTooth - cappedPaid));

                        return {
                            ...plan,
                            paid_amount: cappedPaid,
                            remaining_balance: newRemaining,
                            treatment_status: newRemaining <= 0 ? "completed" : plan.treatment_status
                        };
                    }
                    return plan;
                })
            }));

            const mergedBulkPayments = {
                ...bulkPayments,
                [groupId]: updatedBulk
            };
            setBulkPayments(mergedBulkPayments);
            setShowAddPaymentForm(false);

            if (onChange)
                onChange({
                    ...currentChart,
                    teeth: updatedTeeth,
                    bulkPayments: Object.values(mergedBulkPayments)
                });

        } else {
            // 🦷 Single treatment payment
            const updatedTeeth = (currentChart.teeth || []).map(tooth => ({
                ...tooth,
                treatments: (tooth.treatments || []).map(plan => {
                    if (plan.treatment_id === selectedTreatment.treatment_id) {
                        const prevPaid = formatTo2Decimals(plan.paid_amount || 0);
                        const finalCost = formatTo2Decimals(plan.final_cost || plan.cost || 0);
                        const paymentAmount = formatTo2Decimals(payment.amount || 0);

                        const paidAmount = formatTo2Decimals(prevPaid + paymentAmount);
                        const remainingBalance = formatTo2Decimals(Math.max(0, finalCost - paidAmount));
                        const newStatus = remainingBalance <= 0 ? "completed" : plan.treatment_status === "planned" ? "in-progress" : plan.treatment_status;

                        return {
                            ...plan,
                            paid_amount: paidAmount,
                            remaining_balance: remainingBalance,
                            treatment_status: newStatus,
                            dental_payment_history: [...(plan.dental_payment_history || []), payment]
                        };
                    }
                    return plan;
                })
            }));

            const updatedChart = { ...currentChart, teeth: updatedTeeth };

            setShowAddPaymentForm(false);
            if (onChange) onChange(updatedChart);
        }
    };

    const toggleExpandPayments = (treatment_id) => {
        setExpandedPayments(prev => ({
            ...prev,
            [treatment_id]: !prev[treatment_id]
        }));
    };

    const formatCurrency = (val) => (isNaN(val) ? "0.00" : Number(val).toFixed(2));

    const groupPlans = (plans) => {
        const groups = {};
        plans.forEach(p => {
            const key = p.bulk_group || `single-${p.treatment_id}`;
            if (!groups[key]) {
                groups[key] = { bulk_group: p.bulk_group, items: [] };
            }
            groups[key].items.push(p);
        });
        return Object.values(groups);
    };

    const getDisplayableTreatments = () => {
        const grouped = groupPlans(getAllTreatments(currentChart));
        let displayRows = [];

        grouped.forEach(group => {
            if (group.bulk_group) {
                displayRows.push({ type: "bulk", group });
            } else {
                group.items.forEach(plan => displayRows.push({ type: "single", plan }));
            }
        });

        return displayRows.sort((a, b) => {
            const dateA = new Date(a.type === "bulk" ? a.group.items[0]?.treatment_date : a.plan?.treatment_date);
            const dateB = new Date(b.type === "bulk" ? b.group.items[0]?.treatment_date : b.plan?.treatment_date);
            return dateB - dateA;
        });
    };

    const [totalCount, setTotalCount] = useState(0);
    const displayableRows = getDisplayableTreatments();
    const indexOfLast = currentPage * pageSize;
    const indexOfFirst = indexOfLast - pageSize;
    const currentDisplayRows = displayableRows.slice(indexOfFirst, indexOfLast);

    useEffect(() => {
        const groupedRows = getDisplayableTreatments();
        setTotalCount(groupedRows.length);

        const totalPages = Math.ceil(groupedRows.length / pageSize);
        if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
    }, [currentChart, pageSize]);

    const renderSingleTreatmentRow = (plan) => (
        <React.Fragment key={plan.treatment_id}>
            <tr>
                <td className="fw-bold">{plan.tooth_number}</td>
                <td><Badge bg="secondary">{plan.condition}</Badge></td>
                <td><Badge bg="secondary">Single</Badge></td>
                <td>{plan.treatment_type}</td>
                <td>{formatDisplayDate(plan.treatment_date)}</td>
                <td>₹{formatCurrency(plan.original_cost || plan.cost)}</td>
                <td>
                    ₹{formatCurrency(plan.final_cost || plan.cost)}
                    {parseFloat(plan.discount_amount) > 0 && (
                        <div><small className="text-success">(-₹{formatCurrency(plan.discount_amount)} discount)</small></div>
                    )}
                </td>
                <td><Badge bg="success">₹{formatCurrency(plan.paid_amount)}</Badge></td>
                <td>
                    {plan.remaining_balance > 0
                        ? <Badge bg="warning">₹{formatCurrency(plan.remaining_balance)}</Badge>
                        : <Badge bg="success">Paid</Badge>}
                </td>
                <td>
                    <Badge bg={
                        plan.treatment_status === "completed" ? "success" : plan.treatment_status === "in-progress" ? "warning" : "info"
                    }>
                        {plan.treatment_status}
                    </Badge>
                </td>
                <td>{plan.note}</td>
                <td>
                    <Row className="g-2">
                        <Col xs={6}>
                            <Button className="w-100" size="sm" variant="outline-secondary" onClick={() => toggleExpandPayments(plan.treatment_id)}>
                                {expandedPayments[plan.treatment_id] ? "Hide Pay" : "View Pay"}
                            </Button>
                        </Col>
                        <Col xs={6}>
                            {plan.treatment_status !== "completed" && plan.remaining_balance > 0 && (
                                <Button className="w-100" size="sm" variant="outline-primary" onClick={() => handleOpenAddPaymentForm(plan)}>
                                    Part Pay
                                </Button>
                            )}
                        </Col>
                    </Row>
                </td>
            </tr>

            {expandedPayments[plan.treatment_id] && (
                <tr>
                    <td colSpan="12">
                        <Table size="sm" bordered>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Invoice Number</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plan.dental_payment_history?.length > 0 ? (
                                    plan.dental_payment_history.map((payment, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{payment.invoice_number || "- (pending)"}</td>
                                            <td>{formatDisplayDate(payment.date)}</td>
                                            <td>₹{formatCurrency(payment.amount)}</td>
                                            <td>{amountPaidVia[payment.amount_paid_via]}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="5" className="text-center">No payments recorded</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </td>
                </tr>
            )}
        </React.Fragment>
    );

    const renderBulkGroupRows = (group) => {
        const first = group.items[0];
        const summary = bulkPayments[group.bulk_group] || {};
        const aggregated = group.items.reduce(
            (acc, item) => {
                acc.original_cost += parseFloat(item.original_cost || item.cost || 0);
                acc.final_cost += parseFloat(item.final_cost || item.cost || 0);
                acc.paid_amount += parseFloat(item.paid_amount || 0);
                return acc;
            },
            { original_cost: 0, final_cost: 0, paid_amount: 0 }
        );
        const combined = { ...aggregated, ...summary };
        combined.remaining_balance = combined.final_cost - combined.paid_amount;

        const teethList = group.items.map(p => p.tooth_number).sort((a, b) => a - b);
        const teethDisplay = teethList.length > 10
            ? `${teethList.slice(0, 10).join(", ")} ... (+${teethList.length - 10} more)`
            : teethList.join(", ");

        return (
            <React.Fragment key={group.bulk_group}>
                <tr style={{ backgroundColor: "#f0f9ff" }}>
                    <td colSpan={4} className="fw-bold">
                        🦷 {first.treatment_type} Bulk Treatment
                        <br />
                        <small className="text-muted">Teeth: {teethDisplay} ({group.items.length} total)</small>
                    </td>
                    <td>{formatDisplayDate(first.treatment_date)}</td>
                    <td>₹{formatCurrency(combined.original_cost)}</td>
                    <td>₹{formatCurrency(combined.final_cost)}</td>
                    <td><Badge bg="success">₹{formatCurrency(combined.paid_amount)}</Badge></td>
                    <td>
                        {combined.remaining_balance > 0 ? <Badge bg="warning">₹{formatCurrency(combined.remaining_balance)}</Badge> : <Badge bg="success">Paid</Badge>}
                    </td>
                    <td>
                        <Badge bg={first.treatment_status === "completed" ? "success" : first.treatment_status === "in-progress" ? "warning" : "info"}>
                            {first.treatment_status}
                        </Badge>
                    </td>
                    <td>{first.note}</td>
                    <td>
                        <Row className="g-2">
                            <Col xs={6}>
                                <Button className="w-100" size="sm" variant="outline-secondary" onClick={() => toggleExpandPayments(group.bulk_group)}>
                                    {expandedPayments[group.bulk_group] ? "Hide Pay" : "View Pay"}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                {combined.remaining_balance > 0 && (
                                    <Button
                                        className="w-100" size="sm" variant="outline-primary"
                                        onClick={() =>
                                            handleOpenAddPaymentForm(
                                                {
                                                    bulk_group: group.bulk_group,
                                                    remaining_balance: combined.remaining_balance,
                                                    isBulk: true
                                                },
                                                group.bulk_group
                                            )
                                        }>
                                        Part Pay
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </td>
                </tr>

                {expandedPayments[group.bulk_group] && (
                    <tr>
                        <td colSpan="12">
                            <Table size="sm" bordered>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Invoice Number</th>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>Method</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(combined.dental_payment_history || []).length > 0 ? (
                                        combined.dental_payment_history.map((payment, index) => (<tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{payment.invoice_number || ""}</td>
                                            <td>{formatDisplayDate(payment.date)}</td>
                                            <td>₹{formatCurrency(payment.amount)}</td>
                                            <td>{amountPaidVia[payment.amount_paid_via]}</td>
                                        </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="5" className="text-center">No payments recorded</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    useEffect(() => {
        if (currentChart && currentChart.bulkPayments && Array.isArray(currentChart.bulkPayments)) {
            const bulkMap = {};
            currentChart.bulkPayments.forEach(bp => {
                bulkMap[bp.bulk_group] = bp;
            });
            setBulkPayments(bulkMap);
        } else {
            setBulkPayments({});
        }
    }, [currentChart]);

    return (
        <>
            <Carousel
                variant="dark"
                controls={previousDentalChart.length > 1}
                indicators={false}
                interval={null}
                activeIndex={previousDentalChart.findIndex(chart => chart.chart_id === currentChartId)}
                onSelect={(selectedIndex) => {
                    const selectedChart = previousDentalChart[selectedIndex];
                    if (selectedChart) {
                        setCurrentChartId(selectedChart.chart_id);
                    }
                }}
            >
                {previousDentalChart.map((chart) => {
                    const teethData = getTeethData(chart.chart_type);

                    return (
                        <Carousel.Item key={chart.chart_id}>
                            <div className="chart-header mb-3">
                                <h3 className="text-center">{getChartTitle(chart.chart_type)}</h3>
                                <p className="text-center text-muted">{getChartDescription(chart.chart_type)}</p>
                            </div>

                            {/* Quadrant Layout */}
                            <div>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <div className="quadrant-section">
                                            <h6 className="text-center mb-3">Upper Right</h6>
                                            <div className="d-flex justify-content-center flex-wrap">
                                                {teethData.upper.slice(0, Math.floor(teethData.upper.length / 2)).map(tooth => (
                                                    <div key={tooth.number} className="tooth-wrapper">
                                                        <Tooth
                                                            tooth={tooth}
                                                            condition={getCondition(chart, tooth.number)}
                                                            isSelected={false}
                                                            bulkMode={false}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="quadrant-section">
                                            <h6 className="text-center mb-3">Upper Left</h6>
                                            <div className="d-flex justify-content-center flex-wrap">
                                                {teethData.upper.slice(Math.floor(teethData.upper.length / 2)).map(tooth => (
                                                    <div key={tooth.number} className="tooth-wrapper">
                                                        <Tooth
                                                            tooth={tooth}
                                                            condition={getCondition(chart, tooth.number)}
                                                            isSelected={false}
                                                            bulkMode={false}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="quadrant-section">
                                            <h6 className="text-center mb-3">Lower Right</h6>
                                            <div className="d-flex justify-content-center flex-wrap">
                                                {teethData.lower.slice(Math.floor(teethData.lower.length / 2)).reverse().map(tooth => (
                                                    <div key={tooth.number} className="tooth-wrapper">
                                                        <Tooth
                                                            tooth={tooth}
                                                            condition={getCondition(chart, tooth.number)}
                                                            isSelected={false}
                                                            bulkMode={false}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="quadrant-section">
                                            <h6 className="text-center mb-3">Lower Left</h6>
                                            <div className="d-flex justify-content-center flex-wrap">
                                                {teethData.lower.slice(0, Math.floor(teethData.lower.length / 2)).reverse().map(tooth => (
                                                    <div key={tooth.number} className="tooth-wrapper">
                                                        <Tooth
                                                            tooth={tooth}
                                                            condition={getCondition(chart, tooth.number)}
                                                            isSelected={false}
                                                            bulkMode={false}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Treatment Plans Table */}
                            {displayableRows.length > 0 && (
                                <div className="treatment-plans-section mt-3 mb-3">
                                    <h5 className="mb-3">Treatment Plans</h5>
                                    <Table bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Tooth</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Condition</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Type</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Treatment</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Date</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Original Cost</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Final Cost</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Paid</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Balance</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Status</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Notes</th>
                                                <th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentDisplayRows.map((row) =>
                                                row.type === "bulk" ? renderBulkGroupRows(row.group) : renderSingleTreatmentRow(row.plan)
                                            )}
                                        </tbody>
                                    </Table>

                                    <CustomPagination
                                        totalCount={totalCount}
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>
                            )}
                        </Carousel.Item>
                    );
                })}
            </Carousel>

            {/* Chart Legend */}
            <div className="dental-chart-legend mt-3">
                <h6>Legend</h6>
                <div className="row">
                    {toothConditions.map(({ value, label, color }) => (
                        <div className="col-md-4" key={value}>
                            <div className="legend-item d-flex align-items-center mb-2">
                                <div
                                    className="legend-color"
                                    style={{
                                        width: "20px",
                                        height: "20px",
                                        backgroundColor: color,
                                        border: color === "#ffffff" || color === "#e9ecef" ? "1px solid #333" : "none",
                                        marginRight: "10px"
                                    }}
                                ></div>
                                <span>{label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Dental Payment Form Modal */}
            {showAddPaymentForm && selectedTreatment && (
                <AddDentalPaymentForm
                    show={showAddPaymentForm}
                    treatmentPlan={selectedTreatment}
                    onSave={handleSavePayment}
                    onCancel={() => setShowAddPaymentForm(false)}
                />
            )}
        </>
    );
};

export default PreviousDentalChart;
